<template>
  <div>
    <div class="row">
      <div class="col-xs-12">
        <h2 class="island">
          {{ lang.get('judging.fast_start.type_of_judging.title') }}
        </h2>
      </div>
    </div>
    <div class="row">
      <div class="col-xs-12 col-md-6">
        <judging-modes
          v-model="mode"
          :all-modes="allModes"
          :available-modes="availableModes"
          :show-input="!showNameInput"
          class="mbl"
          @selected="onModeSelected"
        >
          <div
            v-if="showVipJudgingScoreSets"
            slot="details:vip_judging"
            class="island"
          >
            <div class="form-group form-group-subordinate">
              <div class="radio styled">
                <input
                  id="score-set-new"
                  v-model="selection"
                  value="new"
                  name="score-set-selection"
                  type="radio"
                  @change="event => onModeSelected('vip_judging')"
                />
                <label for="score-set-new">
                  {{ lang.get('judging.fast_start.score_set.new') }}
                </label>
              </div>
              <div class="radio styled">
                <input
                  id="score-set-existing"
                  v-model="selection"
                  value="existing"
                  name="score-set-selection"
                  type="radio"
                  @change="clearScoreSet"
                />
                <label for="score-set-existing">
                  {{ lang.get('judging.fast_start.score_set.existing') }}
                </label>
              </div>
              <div
                v-if="showExistingScoreSets"
                class="form-group form-group-subordinate"
              >
                <div
                  v-for="vipJudgingScoreSet in vipJudgingScoreSets"
                  :key="'score-set-' + vipJudgingScoreSet.id"
                  class="radio styled"
                >
                  <input
                    :id="'score-set-' + vipJudgingScoreSet.id"
                    name="score-set"
                    type="radio"
                    :checked="scoreSet && scoreSet.id === vipJudgingScoreSet.id"
                    @change="
                      event =>
                        onScoreSetSelected('vip_judging', vipJudgingScoreSet.id)
                    "
                  />
                  <label :for="'score-set-' + vipJudgingScoreSet.id">
                    {{ vipJudgingScoreSet.name }}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </judging-modes>
        <div v-if="showNameInput" class="row mbl">
          <div class="col-xs-12 col-md-8">
            <div class="form-group">
              <label for="name">
                {{ lang.get('judging.fast_start.name.label') }}
              </label>
              <input
                id="name"
                type="text"
                class="form-control"
                :value="name"
                name="name"
                @input="e => setName(e.target.value)"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="col-xs-12 col-md-6">
        <mode-preview v-show="hasPreview" :mode="mode" />
      </div>
    </div>
    <navigation :back-button="false" :disabled="!ready" v-on="$listeners" />
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import JudgingModes from '@/lib/components/Judging/JudgingModes';
import Navigation from '../Navigation';
import ModePreview from './ModePreview';

const Selection = {
  NEW: 'new',
  EXISTING: 'existing'
};

export default {
  inject: ['lang'],
  components: {
    JudgingModes,
    Navigation,
    ModePreview
  },
  props: {
    allModes: {
      type: Array,
      default: () => []
    },
    availableModes: {
      type: Array,
      default: () => []
    },
    vipJudgingScoreSets: {
      type: Array,
      default: () => []
    },
    votingScoreSetTemplate: {
      type: Object,
      default: () => ({})
    },
    topPickScoreSetTemplate: {
      type: Object,
      default: () => ({})
    },
    galleryScoreSetTemplate: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      mode: null,
      selection: Selection.NEW
    };
  },
  computed: {
    ...mapGetters('judgingFastStart', ['scoreSet']),
    ...mapState('judgingFastStart', ['name']),
    showExistingScoreSets() {
      return this.selection === Selection.EXISTING;
    },
    ready() {
      return this.scoreSet !== null;
    },
    hasPreview() {
      return ['vip_judging', 'top_pick', 'voting', 'qualifying'].includes(
        this.mode
      );
    },
    showVipJudgingScoreSets() {
      return this.mode === 'vip_judging' && this.vipJudgingScoreSets.length > 0;
    },
    showNameInput() {
      return this.mode === 'gallery' || this.mode === 'voting';
    }
  },
  watch: {
    ready(ready) {
      if (!ready) {
        this.$emit('toStep', 'mode');
      }
    }
  },
  created() {
    this.mode = this.scoreSet ? this.scoreSet.mode : null;
    this.selection =
      this.scoreSet && this.scoreSet.id ? Selection.EXISTING : Selection.NEW;
  },
  methods: {
    ...mapMutations('judgingFastStart', [
      'setName',
      'setScoreSet',
      'clearScoreSet',
      'clearScoringCriteria',
      'configureScoringCriterion',
      'deselectRounds'
    ]),
    onModeSelected(mode) {
      this.selection = Selection.NEW;

      if (mode === 'voting') {
        this.setScoreSet({ ...this.votingScoreSetTemplate });
      } else if (mode === 'top_pick') {
        this.setScoreSet({ ...this.topPickScoreSetTemplate });
      } else if (mode === 'gallery') {
        this.setScoreSet({ ...this.galleryScoreSetTemplate });
      } else {
        this.setScoreSet({ mode });
      }

      this.reset();

      this.$emit('modeSelected', mode);
      this.$emit('toStep', 'mode');
    },
    onScoreSetSelected(mode, id) {
      this.setScoreSet({ mode, id });

      this.reset();

      this.$emit('modeSelected', 'vip_judging');
      this.$emit('toStep', 'mode');
    },
    reset() {
      // Once a different score set mode is selected all scoring criteria need to be cleared
      this.clearScoringCriteria();
      this.configureScoringCriterion(null);

      // Rounds need to be deselected, not all score set modes require rounds to be setup
      this.deselectRounds();
    },
    onNext() {
      if (!['vip_judging', 'voting', 'top_pick'].includes(this.mode)) {
        return;
      }

      this.$emit('nextStep', 'scoring');
    }
  }
};
</script>
